import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useShellTier } from '../../layouts/shellTier';
import { Icon } from '../ui/Icon';
import { Button, Dialog, IconButton } from '../ui/primitives';
import { cx } from '../ui/primitives/cx';

/**
 * P-075 · El patrón transversal de búsqueda y filtros de las listas.
 *
 *   [🔍 Buscar…                 ] [Este mes ▾] [Filtros 2]
 *   (Tienda: Centro ×) (Estado: Borrador ×)          Limpiar
 *   23 ventas                               Ordenar: [Más recientes ▾]
 *
 * Reglas (MATRIZ-P075-FILTROS §3 y SINTESIS-P075-UX §4):
 *   · La búsqueda está a la vista cuando la API busca, y se aplica al enviarla (Enter o la lupa) sobre lo
 *     YA aplicado: nunca arrastra un filtro que todavía está en borrador.
 *   · El periodo va a la vista (lo dibuja quien usa la barra: `PeriodField`).
 *   · «Filtros (N)» abre los filtros secundarios en BORRADOR: en el teléfono como hoja (`Dialog`: foco,
 *     Escape y Atrás); en tableta y escritorio como panel debajo de la barra. N = filtros que la persona puso.
 *   · En escritorio, los filtros de todos los días (`quick`: tienda, estado) van a la vista junto al periodo:
 *     donde hay sitio no se esconden, y la lista no baja detrás de un panel abierto.
 *   · Cada filtro aplicado es un chip con su «×», que lo quita y vuelve a pedir.
 *   · «Limpiar» hace lo que la pantalla diga (cada una conserva su semántica de siempre).
 *   · El orden va aparte y a la vista.
 *
 * La barra no sabe nada de los filtros de cada pantalla: solo los presenta. Sin `gap` de flexbox
 * (Chromium 83): la separación es de rejilla o de márgenes (`screens.css`).
 */

export interface FilterChip {
  id: string;
  label: string;
  onRemove: () => void;
}

export interface FilterSearch {
  /** El texto que se está escribiendo (borrador de la búsqueda). */
  value: string;
  onChange: (value: string) => void;
  /** Aplica la búsqueda escrita (vacía = sin búsqueda). */
  onSubmit: (value: string) => void;
  placeholder: string;
  /** Nombre accesible del campo («Buscar compras»). */
  label: string;
  /** El tope de caracteres de la API (pegar un texto largo no puede devolver 400). */
  maxLength?: number;
}

export interface FilterPanel {
  /** Cuántos filtros secundarios aplicados puso la persona. */
  count: number;
  /** Los controles, en borrador. */
  content: ReactNode;
  /** Pide con el borrador. */
  onApply: () => void;
  /** Vuelve el borrador a como arranca la pantalla (no pide: eso lo hace «Ver resultados»). */
  onReset: () => void;
  /** Al abrirse: el borrador se copia de lo aplicado. */
  onOpen?: () => void;
  applying?: boolean;
}

export interface FilterBarProps {
  /** Nombre de la región («Buscar y filtrar ventas»). */
  label: string;
  search?: FilterSearch;
  period?: ReactNode;
  /**
   * Los filtros de todos los días (tienda, estado), a la vista en escritorio, junto al periodo: se aplican
   * al elegirlos, sobre lo aplicado. En teléfono y tableta no se dibujan (viven en «Filtros»).
   */
  quick?: ReactNode;
  panel?: FilterPanel;
  chips?: FilterChip[];
  onClear?: () => void;
  clearLabel?: string;
  /** «23 ventas» (se anuncia al cambiar). */
  summary?: ReactNode;
  /** El control de orden. */
  sort?: ReactNode;
  busy?: boolean;
  className?: string;
}

export function FilterBar({ label, search, period, quick, panel, chips = [], onClear, clearLabel = 'Limpiar', summary, sort, busy = false, className }: FilterBarProps) {
  const tier = useShellTier();
  const sheet = tier === 'compact';
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const wasSheet = useRef(sheet);

  // Al cruzar el corte del teléfono, una hoja abierta no se convierte en panel ni al revés.
  useEffect(() => {
    if (wasSheet.current === sheet) return;
    wasSheet.current = sheet;
    setOpen(false);
  }, [sheet]);

  const toggle = () => {
    if (!open) panel?.onOpen?.();
    setOpen((current) => !current);
  };

  const apply = () => {
    panel?.onApply();
    if (sheet) setOpen(false);
  };

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    search?.onSubmit(search.value.trim());
  };

  const filtersButton = panel && (
    <Button
      aria-controls={sheet ? undefined : panelId}
      aria-expanded={open}
      aria-haspopup={sheet ? 'dialog' : undefined}
      className="n3-filterbar__toggle"
      iconStart={<Icon name="filter" size={18} />}
      onClick={toggle}
      variant={panel.count > 0 ? 'tonal' : 'secondary'}
    >
      Filtros{panel.count > 0 && <span className="n3-filterbar__count"><span className="n3-visually-hidden"> aplicados: </span>{panel.count}</span>}
    </Button>
  );

  const panelFooter = panel && (
    <>
      <Button disabled={panel.applying} onClick={panel.onReset} variant="ghost">Limpiar filtros</Button>
      <Button loading={panel.applying} loadingLabel="Buscando…" onClick={apply} variant="primary">Ver resultados</Button>
    </>
  );

  return (
    <section aria-busy={busy || undefined} aria-label={label} className={cx('n3-filterbar', className)}>
      <div className="n3-filterbar__row">
        {search && (
          <form className="n3-filterbar__search" onSubmit={submitSearch} role="search">
            <span aria-hidden="true" className="n3-filterbar__search-icon"><Icon name="search" size={18} /></span>
            <input
              aria-label={search.label}
              className="n3-filterbar__search-input"
              enterKeyHint="search"
              maxLength={search.maxLength}
              onChange={(event) => search.onChange(event.target.value)}
              placeholder={search.placeholder}
              type="search"
              value={search.value}
            />
            {/* Codex R4 · L: mientras la lista carga, borrar no podría pedir nada (la pantalla lo descartaría): se deshabilita como «Buscar». */}
            {search.value && (
              <IconButton className="n3-filterbar__search-clear" disabled={busy} label="Borrar búsqueda" onClick={() => { search.onChange(''); search.onSubmit(''); }} variant="soft">
                <Icon name="close" size={16} />
              </IconButton>
            )}
            <Button className="n3-filterbar__search-go" disabled={busy} type="submit" variant="secondary">Buscar</Button>
          </form>
        )}
        {(period || filtersButton || (quick && tier === 'wide')) && (
          <div className="n3-filterbar__tools">
            {period}
            {quick && tier === 'wide' && quick}
            {filtersButton}
          </div>
        )}
      </div>

      {panel && !sheet && open && (
        <div className="n3-filterbar__panel" id={panelId}>
          <div className="n3-filterbar__fields">{panel.content}</div>
          <div className="n3-filterbar__panel-actions">{panelFooter}</div>
        </div>
      )}

      {chips.length > 0 && (
        <div className="n3-filterbar__applied">
          <ul aria-label="Filtros aplicados" className="n3-filterbar__chips">
            {chips.map((chip) => (
              <li className="n3-filterbar__chip" key={chip.id}>
                <span className="n3-filterbar__chip-label">{chip.label}</span>
                <button aria-label={`Quitar filtro: ${chip.label}`} className="n3-filterbar__chip-remove" disabled={busy} onClick={chip.onRemove} type="button">
                  <Icon name="close" size={14} />
                </button>
              </li>
            ))}
          </ul>
          {onClear && <Button className="n3-filterbar__clear" disabled={busy} onClick={onClear} variant="ghost">{clearLabel}</Button>}
        </div>
      )}

      {(summary || sort) && (
        <div className="n3-filterbar__meta">
          {summary && <p aria-live="polite" className="n3-filterbar__summary">{summary}</p>}
          {sort && <div className="n3-filterbar__sort">{sort}</div>}
        </div>
      )}

      {panel && sheet && open && (
        <Dialog footer={panelFooter} isBusy={panel.applying} onClose={() => setOpen(false)} title="Filtros">
          <div className="n3-filterbar__fields">{panel.content}</div>
        </Dialog>
      )}
    </section>
  );
}

/** El selector de orden, con la piel del rediseño. */
export function SortSelect({ value, options, onChange, disabled = false, label = 'Ordenar por' }: {
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
  disabled?: boolean;
  label?: string;
}) {
  const id = useId();
  return (
    <span className="n3-sort">
      <label className="n3-sort__label" htmlFor={id}>{label}</label>
      <span className="n3-input n3-input--select n3-sort__control">
        <select className="n3-input__control" disabled={disabled} id={id} onChange={(event) => onChange(event.target.value)} value={value}>
          {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
        <span className="n3-input__chevron"><Icon name="chevron-down" size={18} /></span>
      </span>
    </span>
  );
}

/**
 * Un filtro de todos los días a la vista en escritorio (`FilterBar.quick`): se aplica al elegirlo, sobre lo
 * aplicado. Con la piel del rediseño y su etiqueta encima, como el periodo.
 */
export function FilterQuickSelect({ label, value, options, onChange, disabled = false }: {
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  const id = useId();
  return (
    <span className="n3-filterbar__quick">
      <label className="n3-filterbar__quick-label" htmlFor={id}>{label}</label>
      <span className="n3-input n3-input--select">
        <select className="n3-input__control" disabled={disabled} id={id} onChange={(event) => onChange(event.target.value)} value={value}>
          {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
        <span className="n3-input__chevron"><Icon name="chevron-down" size={18} /></span>
      </span>
    </span>
  );
}
